import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import { UiCheckCircle } from '@vyaguta/vyaguta-icons/ui';

import PopOver from 'components/common/popover';

import { SKILL_PILL_ACTION, SKILL_STATUS } from 'constants/skills';

import SkillRatingSelector from 'pages/settings/skills/components/SkillRatingSelector';

import type { SkillStatusDetail } from 'interface/skillTree';
import type {
  GlobalRatingItem,
  MappedSkillRating,
  OverlaySkill,
  SkillTreeNodeBase,
  SkillPillActionType,
} from '../types';

type Props<TSkill extends SkillTreeNodeBase<TSkill>> = {
  skill: TSkill;
  onClick?: () => void;
  globalRatings?: GlobalRatingItem[];
  mappedSkillRating?: MappedSkillRating;
  selectedSkills?: TSkill[] | null;
  onSelectedSkillsChange?: (skills: TSkill[]) => void;
  setIsOverlayOpenFor?: (skill: OverlaySkill<TSkill> | null) => void;
  isOpenFor?: OverlaySkill<TSkill> | number | string | null;
  isSelf?: boolean;
  isTeamManager?: boolean;
  ratingOpenFor: TSkill | number | string | null;
  setRatingOpenFor: React.Dispatch<React.SetStateAction<TSkill | number | string | null>>;
};

const getStatusDetail = (skillStatus?: SkillStatusDetail | string | null): SkillStatusDetail | null => {
  if (typeof skillStatus === 'object' && skillStatus !== null && 'status' in skillStatus) {
    return skillStatus;
  }

  return null;
};

const SkillPill = <TSkill extends SkillTreeNodeBase<TSkill>>({
  skill,
  onClick,
  globalRatings = [],
  mappedSkillRating,
  selectedSkills,
  onSelectedSkillsChange,
  setIsOverlayOpenFor,
  isOpenFor,
  isSelf,
  isTeamManager,
  ratingOpenFor,
  setRatingOpenFor,
}: Props<TSkill>) => {
  const { name } = skill;

  const ratingScale = useMemo<GlobalRatingItem[]>(() => {
    return skill.localRating?.length ? skill.localRating : globalRatings;
  }, [globalRatings, skill.localRating]);
  const mappedRating = mappedSkillRating && skill.id != null ? mappedSkillRating[String(skill.id)] : null;
  const mappedStatusDetail = getStatusDetail(mappedRating?.skillStatus);
  const sourceKey = `${skill.id ?? ''}:${mappedRating?.rating ?? ''}:${mappedStatusDetail?.status ?? ''}:${
    mappedStatusDetail?.rating ?? ''
  }:${skill.rating ?? ''}`;
  const defaultRating = mappedRating?.rating ?? skill.rating ?? null;
  const [ratingDraft, setRatingDraft] = useState<{ sourceKey: string; value: number | null }>({
    sourceKey,
    value: defaultRating,
  });
  const rating = ratingDraft.sourceKey === sourceKey ? ratingDraft.value : defaultRating;
  const setRating = (value: number | null) => {
    setRatingDraft({ sourceKey, value });
  };
  const status = mappedStatusDetail?.status ?? null;
  const recommendedForDelete = useMemo(() => mappedStatusDetail?.rating === 0, [mappedStatusDetail]);
  const skillStatusDetail = getStatusDetail(skill.skillStatus);
  const openSkillId = typeof isOpenFor === 'object' && isOpenFor !== null ? isOpenFor.id : isOpenFor;
  const ratingOpenForId =
    typeof ratingOpenFor === 'object' && ratingOpenFor !== null ? ratingOpenFor.id : ratingOpenFor;

  const isFlagged = skillStatusDetail?.status === SKILL_STATUS.FLAGGED || status === SKILL_STATUS.FLAGGED;

  const isRemovalRecommendation =
    (skillStatusDetail?.status === SKILL_STATUS.FLAGGED && skillStatusDetail?.rating === 0) ||
    (status === SKILL_STATUS.FLAGGED && recommendedForDelete);

  const isSkillSelected =
    openSkillId === skill.id && (skillStatusDetail?.status === SKILL_STATUS.APPROVED || !skillStatusDetail?.status);

  const isFlaggedSelected = openSkillId === skill.id && skillStatusDetail?.status === SKILL_STATUS.FLAGGED;

  const isDeleteSelected =
    openSkillId === skill.id && skillStatusDetail?.status === SKILL_STATUS.FLAGGED && skillStatusDetail?.rating === 0;

  const dispatcherCallback = (
    payload: { id: number | string; rating: number } | number | string,
    type: SkillPillActionType
  ) => {
    if (!onSelectedSkillsChange) {
      return;
    }

    const currentSelectedSkills = selectedSkills ? [...selectedSkills] : [];

    if (type === SKILL_PILL_ACTION.ADD && typeof payload === 'object') {
      const filteredSkills = currentSelectedSkills.filter((item) => item.id !== payload.id);
      const previousSkill = currentSelectedSkills.find((item) => item.id === payload.id);

      onSelectedSkillsChange([
        ...filteredSkills,
        {
          ...(previousSkill || ({ id: payload.id, name: skill.name, subSkills: [] } as TSkill)),
          id: payload.id,
          rating: payload.rating,
        },
      ]);
    } else if (type === SKILL_PILL_ACTION.REMOVE && (typeof payload === 'number' || typeof payload === 'string')) {
      onSelectedSkillsChange(currentSelectedSkills.filter((item) => item.id !== payload));
    }
  };

  return (
    <PopOver
      disabled={!mappedSkillRating || Boolean(status)}
      visible={Boolean(mappedSkillRating) && !status}
      interactive
      open={ratingOpenForId === skill.id}
      onRequestClose={() => setRatingOpenFor(null)}
      trigger="click"
      useContext={false}
      html={
        <SkillRatingSelector
          skillRatingScale={ratingScale}
          skill={skill}
          rating={rating}
          setRating={setRating}
          dispatcherCallback={dispatcherCallback}
          selectedSkills={selectedSkills || []}
        />
      }
      theme="light"
      offset={20}
      arrow={true}
    >
      <div
        className={classNames('skills-list__pill-item', {
          'skills-list__pill-item--selected': Boolean(rating),
          'skills-list__pill-item--view': Boolean(skill.rating),
          'skills-list__pill-item--hover':
            Boolean(setIsOverlayOpenFor) && ((isSelf && skill.skillStatus) || isTeamManager),
          'skills-list__pill-item--flagged': isFlagged,
          'skills-list__pill-item--removal-recommendation': isRemovalRecommendation,
          'skills-list__pill-item--verified-selected': isSkillSelected,
          'skills-list__pill-item--flagged-selected': isFlaggedSelected,
          'skills-list__pill-item--delete-selected': isDeleteSelected,
        })}
        onClick={(event) => {
          event.stopPropagation();

          onClick?.();
        }}
      >
        <div className="name">{name ?? ''}</div>
        {rating && (
          <div
            className={classNames('rating', {
              'rating--view': Boolean(skill.rating),
            })}
          >
            {rating}
          </div>
        )}
        {(skillStatusDetail?.status === SKILL_STATUS.APPROVED || status === SKILL_STATUS.APPROVED) && (
          <div className="icon-container">
            <UiCheckCircle />
          </div>
        )}
      </div>
    </PopOver>
  );
};

export default SkillPill;
